import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";

import * as client from "../../../client";
import { apiErrorDetail } from "../../../api/errors";
import { useToast } from "../../../context-providers/Toast";
import { useOutlookDropHelper } from "../../../hooks/useOutlookDropHelper";
import { formatCurrency, readableLayer } from "../policy-application/util";
import { useCreateEndorsement, useEstimateEndorsementPremium } from "./data";
import styles from "./Endorsements.module.css";

interface NewEndorsementDialogProps {
  reportId: string;
  applications: client.PolicyApplicationView[];
  layersById: Map<string, client.Layer>;
  // The account's TIV as endorsed today (from the ledger response); null
  // while the ledger is still loading.
  currentTiv: number | null;
  visible: boolean;
  onHide: () => void;
}

type TivEntryMode = "total" | "delta";

const TIV_MODE_OPTIONS: { label: string; value: TivEntryMode }[] = [
  { label: "New total", value: "total" },
  { label: "Change (±)", value: "delta" },
];

const BASIS_OPTIONS: {
  label: string;
  value: client.EndorsementPremiumBasis;
}[] = [
  { label: "Account rate", value: "account_rate" },
  { label: "No premium change", value: "no_change" },
  { label: "Custom amount", value: "custom" },
];

const toIsoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const NewEndorsementDialog: React.FC<NewEndorsementDialogProps> = ({
  reportId,
  applications,
  layersById,
  currentTiv,
  visible,
  onHide,
}) => {
  const toast = useToast();
  const create = useCreateEndorsement(reportId);

  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [description, setDescription] = useState("");
  const [premiumBasis, setPremiumBasis] =
    useState<client.EndorsementPremiumBasis>("account_rate");
  const [changesSov, setChangesSov] = useState(true);
  const [tivMode, setTivMode] = useState<TivEntryMode>("total");
  const [tivInput, setTivInput] = useState<number | null>(null);
  const [customPremium, setCustomPremium] = useState<number | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropBoxRef = useRef<HTMLDivElement>(null);

  const addFiles = (added: File[]) => {
    if (added.length === 0) {
      return;
    }
    // Later picks of the same filename replace the earlier ones.
    setFiles((prev) => [
      ...prev.filter((f) => !added.some((a) => a.name === f.name)),
      ...added,
    ]);
  };

  // Emails dragged straight from Outlook via the desktop helper land here as
  // .msg Files, same shape as a native drop.
  const { helperConnected, helperDragging } = useOutlookDropHelper({
    zoneRef: dropBoxRef,
    onFiles: addFiles,
    enabled: visible,
  });

  const tivTarget =
    !changesSov || tivInput === null
      ? null
      : tivMode === "delta"
        ? currentTiv !== null
          ? Math.round(currentTiv + tivInput)
          : null
        : tivInput;

  const estimate = useEstimateEndorsementPremium({
    reportId,
    policyApplicationId: applicationId,
    effectiveDate: effectiveDate ? toIsoDate(effectiveDate) : null,
    tivTarget,
    enabled: visible && changesSov && premiumBasis === "account_rate",
  });

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const reset = () => {
    setApplicationId(null);
    setEffectiveDate(null);
    setDescription("");
    setPremiumBasis("account_rate");
    setChangesSov(true);
    setTivMode("total");
    setTivInput(null);
    setCustomPremium(null);
    setFiles([]);
  };

  const missingRequired =
    !applicationId ||
    !effectiveDate ||
    !description.trim() ||
    (premiumBasis === "custom" && customPremium === null) ||
    (changesSov && (!tivTarget || tivTarget <= 0 || files.length === 0));

  const handleSubmit = () => {
    if (missingRequired || !applicationId || !effectiveDate) {
      return;
    }
    create.mutate(
      {
        policyApplicationId: applicationId,
        effectiveDate: toIsoDate(effectiveDate),
        description: description.trim(),
        premiumBasis,
        tivTarget,
        customTermPremiumDelta:
          premiumBasis === "custom" ? customPremium : null,
        files,
      },
      {
        onSuccess: (endorsement) => {
          toast.show({
            severity: "success",
            summary: "Endorsement created",
            detail:
              endorsement.status === "generating"
                ? "Building the endorsed schedule — it will land in review when ready."
                : "Endorsement created as a draft.",
            life: 4000,
          });
          reset();
          onHide();
        },
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Could not create the endorsement",
            detail: "Please try again.",
            life: 5000,
          });
        },
      }
    );
  };

  const applicationOptions = applications.map((app) => {
    const layer = layersById.get(app.layer_id);
    return {
      label: `Policy ${app.suffix}${
        app.policy_number ? ` · ${app.policy_number}` : ""
      }${layer ? ` — ${readableLayer(layer)}` : ""}`,
      value: app.id,
    };
  });

  return (
    <Dialog
      header="New Endorsement"
      visible={visible}
      onHide={onHide}
      draggable={false}
      style={{ width: "560px" }}
      footer={
        <div className={styles.footer}>
          <Button label="Cancel" severity="secondary" text onClick={onHide} />
          <Button
            label="Create"
            onClick={handleSubmit}
            disabled={missingRequired}
            loading={create.isPending}
          />
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.field}>
          <span className={styles.label}>Policy</span>
          <Dropdown
            value={applicationId}
            options={applicationOptions}
            onChange={(e) => setApplicationId(e.value)}
            placeholder="Select the policy this endorses"
            style={{ width: "100%" }}
          />
        </div>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <span className={styles.label}>Effective date</span>
            <Calendar
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.value ?? null)}
              showButtonBar
              onClearButtonClick={() => setEffectiveDate(null)}
            />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Premium basis</span>
            <Dropdown
              value={premiumBasis}
              options={BASIS_OPTIONS}
              onChange={(e) => setPremiumBasis(e.value)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        {premiumBasis === "custom" && (
          <div className={styles.field}>
            <span className={styles.label}>
              Premium change (full term; negative for a return premium)
            </span>
            <InputNumber
              value={customPremium}
              onValueChange={(e) => setCustomPremium(e.value ?? null)}
              mode="currency"
              currency="USD"
              locale="en-US"
              maxFractionDigits={0}
              placeholder="$0"
            />
            <span className={styles.premiumHint}>
              The remainder-of-policy amount is prorated from the policy dates;
              amend either number from the ledger afterwards.
            </span>
          </div>
        )}
        <div className={styles.field}>
          <span className={styles.label}>Description</span>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the broker asked for, e.g. add the Fremont warehouse"
            rows={2}
            autoResize
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>
            <Checkbox
              checked={changesSov}
              onChange={(e) => setChangesSov(Boolean(e.checked))}
              style={{ marginRight: "0.5rem" }}
            />
            Changes the schedule of values
          </span>
          {changesSov && (
            <>
              <span className={styles.premiumHint}>
                {currentTiv !== null
                  ? `Current TIV (as endorsed): ${formatCurrency(currentTiv)}`
                  : "Loading current TIV…"}
              </span>
              <div className={styles.fieldRow}>
                <SelectButton
                  value={tivMode}
                  options={TIV_MODE_OPTIONS}
                  onChange={(e) => e.value && setTivMode(e.value)}
                  allowEmpty={false}
                  disabled={currentTiv === null}
                />
                <InputNumber
                  value={tivInput}
                  onValueChange={(e) => setTivInput(e.value ?? null)}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  maxFractionDigits={0}
                  placeholder={
                    tivMode === "delta"
                      ? "TIV change, e.g. 5,000,000 or -2,000,000"
                      : "New TIV target"
                  }
                />
              </div>
              {tivMode === "delta" && tivTarget !== null && (
                <span className={styles.premiumHint}>
                  New TIV target: {formatCurrency(tivTarget)}
                </span>
              )}
            </>
          )}
          {changesSov && premiumBasis === "account_rate" && estimate.error && (
            <span className={styles.premiumHint}>
              Premium estimate unavailable:{" "}
              {apiErrorDetail(estimate.error) ?? "could not compute."}
            </span>
          )}
          {changesSov && premiumBasis === "account_rate" && estimate.data && (
            <span className={styles.premiumHint}>
              Estimated premium change at account rate:{" "}
              {estimate.data.term_premium_delta < 0 ? "−" : "+"}
              {formatCurrency(Math.abs(estimate.data.term_premium_delta))} for
              the full term,{" "}
              {estimate.data.prorated_premium_delta < 0 ? "−" : "+"}
              {formatCurrency(
                Math.abs(estimate.data.prorated_premium_delta)
              )}{" "}
              for the remainder of the policy. Final numbers come from the
              generated schedule.
            </span>
          )}
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Documents</span>
          <div
            ref={dropBoxRef}
            className={`${styles.dropBox} ${
              isDragOver || helperDragging ? styles.dropBoxActive : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="pi pi-cloud-upload" />
            <span>
              Drag the endorsement documents here or click to browse — .eml and
              .msg emails are expanded into their attachments
            </span>
            {helperConnected && (
              <span className={styles.helperHint}>
                <i className="pi pi-bolt" /> Outlook drag-and-drop enabled
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInput}
              className={styles.hiddenInput}
            />
          </div>
          {files.length > 0 && (
            <div className={styles.pendingFiles}>
              {files.map((file) => (
                <div key={file.name} className={styles.pendingFile}>
                  <span className={styles.pendingFileName}>{file.name}</span>
                  <Button
                    icon="pi pi-times"
                    text
                    rounded
                    size="small"
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      setFiles((prev) => prev.filter((f) => f !== file))
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
